<template>
    <div id="nav" :class="{ embedded: isEmbedded }">
        <div class="nav-left">
            <back-button v-if="showBack"/>
            <template v-else>
                <router-link to="/library">
                    <div class="home-item" :class="{ inactive: $route.path.startsWith('/admin') }">
                        <img :alt="`${appName} logo`" id="logo" :src="logoSquareUrl"/>
                        <div class="text">
                            <div class="org" v-if="orgExists" :title="org.name">{{ org.name }}</div>
                            <div class="home">{{ appName }} Home</div>
                        </div>
                    </div>
                </router-link>
                <router-link v-if="isAdminOrg" to="/admin" class="admin-link">
                    <i class="far fa-tools"></i>Administration
                </router-link>
            </template>
        </div>
        <div class="nav-right" v-if="userExists">
            <div class="settings" title="Go to settings" @click="goToSettings">
                <span class="icon far fa-cog"></span>
            </div>
            <div class="user" :class="{ admin: isSuperAdmin }" id="user">{{ initials }}</div>
            <elem-dropdown triggerSelector="#user" :offset="[0, 5]">
                <div class="user-dropdown">
                    <div class="top">
                        <div class="initials" :class="{ admin: isSuperAdmin }">{{ initials }}</div>
                        <div class="name">{{ user.name }}</div>
                        <div class="email">{{ user.email }}</div>
                        <button v-if="!isEmbedded" @click="onLogout" class="logout">
                            {{ isSuperAdmin ? 'Back to Administration' : 'Log Out' }}
                        </button>
                    </div>
                    <div class="separator"></div>
                    <div class="bottom">
                        <a href='https://kb.paperworkforms.com/terms-and-policies/terms-of-service' target='_blank' class="subtle">Terms of Service</a>
                    </div>
                </div>
            </elem-dropdown>
        </div>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';

    export default {
        name: 'NavBar',
        data() {
            return {};
        },
        computed: {
            ...mapGetters({
                showNav: 'layout/showNav',
                userExists: 'user/exists',
                user: 'user/user',
                orgExists: 'org/exists',
                org: 'org/org',
                isEmbedded: 'base/isEmbedded',
                isAdminOrg: 'org/isAdmin',
                isSuperAdmin: 'auth/isSuperAdmin',
                appName: 'base/appName',
                logoSquareUrl: 'base/logoSquareUrl'
            }),
            showBack() {
                let match = [
                    '/viewer',
                    '/settings',
                    '/apitest',
                    '/styleguide',
                ].filter((path) => {
                    return this.$route.path.startsWith(path);
                });

                return !!match.length;
            },
            initials() {
                let initials;
                if (this.user.fname && this.user.lname) {
                    initials =
                        this.user.fname.substr(0, 1) + this.user.lname.substr(0, 1);
                } else {
                    initials = this.user.email.substr(0, 2);
                }
                return initials.toUpperCase();
            },
        },
        methods: {
            goToSettings() {
                this.$router.push(this.isSuperAdmin ? '/settings/organization' : '/settings');
            },
            onLogout() {
                if (this.isSuperAdmin) {
                    this.$store.dispatch('admin/logoutOfOrg');
                } else {
                    this.$store.dispatch('auth/logout');
                }
            }
        },
    };
</script>

<style lang="scss" scoped>
    #nav {
        z-index: 999;
        position: relative;
        top: 0;
        left: 0;
        height: $height-nav;
        width: 100%;
        display: flex;
        justify-content: space-between;
        flex-direction: row;
        flex-shrink: 0;
        text-align: left;
        background-color: $color-white;
        border-bottom: 1px solid $color-border;
        user-select: none;

        // Style overrides for embedded mode
        &.embedded {
            .home-item {
                background-color: $color-disabled-bg;
                color: $color-black;

                &:hover {
                    background-color: darken($color-disabled-bg, 3.5);
                }

                .text {
                    color: $color-black;
                }
            }
        }

        .nav-left {
            height: 100%;
            display: flex;

            ::v-deep button {
                margin-left: 1em;
            }
        }

        .nav-right {
            @include flex-split;
        }
    }

    .item {
        cursor: pointer;
        font-weight: $bold;
        display: inline-flex;
        position: relative;
        align-items: center;
        height: 100%;
        padding: 0.5em 0.75em;
        white-space: nowrap;
        text-decoration: none;
        color: inherit;
        transition: color 0.2s ease;
        overflow: hidden;
        user-select: none;

        &:after {
            content: '';
            position: absolute;
            transform: translateY(0.15em);
            opacity: 0;
            transition: transform 0.2s ease-in-out, opacity 0.2s;
            background: $color-highlight;
            width: calc(100% - 1.5em);
            height: 3px;
            bottom: 0.6em;
            left: 0.75em;
        }

        &.router-link-exact-active,
        &.router-link-active:not(.item-home) {
            color: $color-highlight;

            &:after {
                transform: translateY(0);
                opacity: 1;
            }
        }

        &:not(.router-link-active):hover,
        &.item-home:not(.router-link-exact-active):hover {
            &:after {
                transform: translateY(0);
                opacity: 1;
            }
        }
    }

    .home-item {
        display: flex;
        align-items: center;
        height: 100%;
        width: 250px;
        padding: 12px;
        white-space: nowrap;
        color: $color-white;
        background-color: $color-main;
        transition: background-color 0.2s;
        cursor: pointer;

        &:hover {
            background-color: darken($color-main, 4);
        }

        #logo {
            height: 30px;
            max-width: 100%;
            border-radius: 6px;
            margin-right: 8px;
            user-select: none;
            background-color: transparent;
            border: 1px solid $color-main;
        }

        .text {
            display: flex;
            flex-direction: column;
            line-height: 0.8rem;
            overflow: hidden;

            .org {
                text-transform: uppercase;
                font-size: 0.6rem;
                overflow: hidden;
                text-overflow: ellipsis;
            }
        }

        &.inactive {
            color: $color-main;
            background-color: transparent;

            &:hover {
                background-color: rgba(0, 0, 0, 0.07);
            }
        }
    }

    .settings,
    .user {
        @include circle($height-nav - 15);

        border: 1px solid transparent;
        color: $color-disabled-text;
        transition: background-color 0.2s, color 0.2s, border-color 0.2s;
        cursor: pointer;
        margin: 0 10px;

        &:hover, &.admin:hover {
            color: $color-label;
            background-color: $color-white;
            border-color: $color-border;
        }
    }

    .settings {
        font-size: 23px;
        transition: border-color 0.5s;
    }

    .user {
        background-color: $color-main;
        color: $color-white;
        border: 1px solid $color-main;

        &.admin {
            background-color: $color-admin-main;
        }
    }

    .user-dropdown {
        min-width: 250px;
        padding: 0.35em 0;

        .top {
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 0.2em 0.5em;

            .initials {
                @include circle(75px);
                color: $color-white;
                background-color: $color-main;

                &.admin {
                    background-color: $color-admin-main;
                }
            }

            .name {
                margin-top: 0.5em;
                font-size: $font-size-larger;
            }

            .email {
                margin-top: 0.2em;
                color: $color-label;
            }

            .logout {
                margin-top: 1em;
            }
        }

        .separator {
            width: 100%;
            height: 1px;
            background: $color-border;
            margin: 0.5em 0;
        }

        .bottom {
            width: 100%;
            display: flex;
            justify-content: flex-end;
            align-items: center;
            white-space: nowrap;
            color: $color-label;
            font-size: $font-size-smaller;

            > a {
                flex-basis: 0;
                margin: 0 5px;
            }
        }
    }

    .admin-link {
        height: 100%;
        padding: 0 1.5em;
        color: $color-admin-main;
        display: flex;
        align-items: center;
        border-bottom: 2px solid transparent;
        border-top: 2px solid transparent;

        &:hover {
            background-color: rgba(0, 0, 0, 0.07);
        }

        &.router-link-active {
            border-bottom-color: $color-admin-main;
        }

        i {
            font-size: 1.1em;
        }
    }
</style>
